/**
 * @fileoverview Module-level version registry for `public-cdn` assets.
 *
 * `cdn(key)` consults this registry to append a `?v=<version>` query string,
 * which lets us bust the browser/Supabase CDN cache as soon as an admin
 * replaces a file. The map is populated by `CdnVersionProvider` (RPC
 * `get_cdn_versions`) and refreshed on focus, on demand, and via a
 * cross-tab BroadcastChannel signal.
 */

let versions: Record<string, string> = {};
const listeners = new Set<() => void>();

/** Replace the in-memory map and notify subscribers. */
export function setCdnVersions(next: Record<string, string>): void {
  versions = next;
  listeners.forEach((fn) => fn());
}

/** Patch a single key (used after admin uploads for instant local feedback). */
export function setCdnVersion(key: string, version: string): void {
  versions = { ...versions, [key]: version };
  listeners.forEach((fn) => fn());
}

export function getCdnVersion(key: string): string | undefined {
  return versions[key];
}

export function subscribeCdnVersions(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Cross-tab notifier: admin upload in tab A → preview in tab B refreshes. */
const CHANNEL_NAME = "cdn-versions";

export function broadcastCdnVersionBump(payload?: { key?: string; version?: string }): void {
  if (typeof BroadcastChannel === "undefined") return;
  try {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    ch.postMessage(payload ?? {});
    ch.close();
  } catch {
    /* noop */
  }
}

export function onCdnVersionBroadcast(handler: (payload: { key?: string; version?: string }) => void): () => void {
  if (typeof BroadcastChannel === "undefined") return () => {};
  const ch = new BroadcastChannel(CHANNEL_NAME);
  const listener = (ev: MessageEvent) => handler(ev.data ?? {});
  ch.addEventListener("message", listener);
  return () => {
    ch.removeEventListener("message", listener);
    ch.close();
  };
}
